import { useState } from "react";
import {
  Box, Paper, Stack, Button, MenuItem, TextField, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { purchaseOrdersApi, type PurchaseOrderListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("procurement.po.create"));

  const query = useQuery({
    queryKey: ["purchase-orders", status, pagination.page, pagination.pageSize],
    queryFn: () => purchaseOrdersApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<PurchaseOrderListDto>[] = [
    { field: "documentNo", headerName: "Document #", width: 140 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
    { field: "vendorName", headerName: "Vendor", flex: 1, minWidth: 200 },
    {
      field: "status", headerName: "Status", width: 130,
      renderCell: (p) => <StatusPill label={p.value as string} />,
    },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
    {
      field: "totalAmount", headerName: "Total", width: 140, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)),
    },
    { field: "expectedDeliveryDate", headerName: "Expected", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Procurement", "Purchase orders"]}
        title="Purchase orders"
        actions={
          canCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/procurement/purchase-orders/new")}>
              New PO
            </Button>
          ) : undefined
        }
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map((s) => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/procurement/purchase-orders/${p.row.id}`)}
            sx={{ cursor: "pointer" }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
